import {
  fuzzyMatchHighlight,
  fuzzyMatchWithMapping,
} from '@wandb/weave/common/util/fuzzyMatch';
import {type Option} from '@wandb/weave/common/util/uihelpers';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, type IconName} from '@wandb/weave/components/Icon';
import {partition} from 'lodash';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {type DropdownItemProps, Icon as SemanticIcon} from 'semantic-ui-react';

import {keyDisplayName, keyFromString, keyStringDisplayName} from './runs';
import {type Key} from './runTypes';

export * from '@wandb/weave/common/util/uihelpers';

const KEY_LIMIT = 100;

export const UI_TAG_TEXT = 'tag';

export interface DropdownOption {
  key: string;
  text: string;
  value: string;
}

export const maybePluralizeWord = (count: number, noun: string, suffix = 's') =>
  `${noun}${count !== 1 ? suffix : ''}`;

export const maybePluralize = (
  count: number,
  noun: string,
  suffix = 's',
  shouldUseLocaleString = false
) =>
  `${
    shouldUseLocaleString ? count.toLocaleString() : count
  } ${maybePluralizeWord(count, noun, suffix)}`;

// Take a dropdown option and beautifies it.
// Adding an image and casting the text
export function beautify(item: Option): Option {
  const key = keyFromString(item.key);
  const image = key ? getImage(key) : <SemanticIcon name="tasks" />;
  const text = key ? keyDisplayName(key, true) : item.key;

  return {
    ...item,
    text: image ? (
      <span className="dropdown-with-icon">
        {image} {text}
      </span>
    ) : (
      text
    ),
  };
}

export const searchFunction = (options: DropdownItemProps[], query: string) => {
  const matched = fuzzyMatchWithMapping(options, query, o =>
    o.key.toString()
  ).slice(0, KEY_LIMIT);
  return matched.map(o => {
    const key = keyFromString(o.key);
    return beautify({
      key,
      value: key ?? o.key,
      ...o,
      text:
        key != null ? fuzzyMatchHighlight(keyDisplayName(key), query) : o.key,
    });
  });
};

export const getImage = (key: Key) => {
  return <Icon name={getIconNameForKey(key)} />;
};

export const getIconNameForKey = (key: Key): IconName => {
  if (key.section === 'run') {
    switch (key.name) {
      // Note fallthrough:
      case 'createdAt':
      case 'heartbeatAt':
      case 'updatedAt':
        return 'date';
      case 'duration':
        return 'history';
      case 'displayName':
        return 'text-language';
      case 'notes':
        return 'type-string-alt';
      case 'status':
        return 'spiral';
      case 'username':
        return 'user-author';
      default:
        return 'list';
    }
  }

  switch (key.section) {
    case 'config':
      return 'settings';
    case 'summary':
      return 'document';
    case 'tags':
      return 'tag';
    default:
      return 'document';
  }
};

export const isTag = (v: string) => {
  return v.split(':')[0] === 'tags';
};

export function partitionTags(values: string[]): [string[], string[]] {
  return partition<string>(values, isTag);
}

export function tagOption(v: string): Option {
  return beautify({
    text: UI_TAG_TEXT,
    key: v,
    value: v,
  });
}

export function makeOptions(values: string[]) {
  return values.map(v => ({text: v, key: v, value: v}));
}

export function makeOptionsFromKeyStrings(values: string[]) {
  return makeOptions(values).map(option => ({
    ...option,
    text: keyStringDisplayName(option.text),
  }));
}

// e.persist() is necessary to use setTimeout in an event handler (like with _.debounce or _.throttle)
// see Event Pooling here: https://reactjs.org/docs/events.html
export function handleAsyncEvent(
  e: React.ChangeEvent<HTMLInputElement>,
  eventHandler: Function
) {
  e.persist();
  eventHandler(e);
}
